/**
 * Hook to identify users in Microsoft Clarity for session analytics
 * 
 * Sets custom tags on the Clarity session when a user is authenticated.
 * These tags enable filtering sessions in the Clarity dashboard by user attributes.
 * 
 * Privacy note: Only non-PII data is sent (userId, account age, admin status).
 * No email, name, or other personal information is transmitted.
 * 
 * @see https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
 */
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

// Debug flags - set to true to verify deployment, then disable for production
const CLARITY_DEBUG = true;
const CLARITY_VERBOSE = false;

// Module-level tracking to persist across component remounts
let lastIdentifiedUserId: number | null = null;

/**
 * Calculate account age in days from creation date
 */
const calculateAccountAgeDays = (createdAt: string): number => {
  const created = new Date(createdAt);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Identifies the current user in Microsoft Clarity
 * 
 * Safe to call multiple times - will only identify once per session.
 * Safe when Clarity is not installed - silently does nothing.
 */
export const useClarityIdentify = (): void => {
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
  const userData = useSelector((state: RootState) => state.user.userData);
  const isAdmin = useSelector((state: RootState) => state.user.isAdmin);

  useEffect(() => {
    if (CLARITY_VERBOSE) {
      console.log('[Clarity] Hook running:', {
        isAuthenticated,
        userId: userData?.userId,
        lastIdentifiedUserId,
        hasClarity: typeof window !== 'undefined' && !!window.clarity
      });
    }

    // Only run client-side
    if (typeof window === 'undefined') {
      if (CLARITY_VERBOSE) console.log('[Clarity] Skipped: SSR');
      return;
    }
    
    // Only identify authenticated users with loaded data
    if (!isAuthenticated || !userData?.userId) {
      if (CLARITY_VERBOSE) console.log('[Clarity] Skipped: Not authenticated or no userId');
      return;
    }
    
    // Skip if already identified this user (prevents duplicate calls)
    if (lastIdentifiedUserId === userData.userId) {
      if (CLARITY_VERBOSE) console.log('[Clarity] Skipped: Already identified user', userData.userId);
      return;
    }
    
    // Check if Clarity is installed
    if (!window.clarity) {
      console.log('[Clarity] window.clarity not found - is the script installed?');
      return;
    }

    // Calculate account age for cohort analysis
    const accountAgeDays = userData.createdAt 
      ? calculateAccountAgeDays(userData.createdAt) 
      : 0;

    const tags = {
      isAdmin: String(isAdmin),
      hasUsedFreeProject: String(userData.freeProjectUsed ?? false),
      accountAgeDays: String(accountAgeDays),
    };

    // Build friendly name for Clarity UI (e.g., "User 42" or "Admin 42")
    const friendlyName = isAdmin 
      ? `Admin ${userData.userId}` 
      : `User ${userData.userId}`;

    // Use Clarity's identify API to set the custom user ID
    // This shows directly in the "User ID" column in Clarity's UI
    // Syntax: clarity("identify", customUserId, customSessionId, customPageId, friendlyName)
    // Pass null for optional session/page IDs, include friendlyName to display in UI
    window.clarity('identify', String(userData.userId), null, null, friendlyName);

    // Set additional custom tags for filtering
    window.clarity('set', 'userId', String(userData.userId));
    window.clarity('set', 'userDisplayName', friendlyName);
    window.clarity('set', 'isAdmin', tags.isAdmin);
    window.clarity('set', 'hasUsedFreeProject', tags.hasUsedFreeProject);
    window.clarity('set', 'accountAgeDays', tags.accountAgeDays);

    // Mark as identified to prevent duplicate calls
    lastIdentifiedUserId = userData.userId;

    if (CLARITY_DEBUG) {
      console.log('[Clarity] User identified:', { 
        userId: userData.userId, 
        friendlyName, 
        identifyCall: `clarity('identify', '${userData.userId}', null, null, '${friendlyName}')`,
        tags 
      });
    }

  }, [isAuthenticated, userData, isAdmin]);
};

export default useClarityIdentify;